import { FilterOperators } from "@crispico/foundation-gwt-js";
import { EntityDescriptor, EntityTablePage, EntityTablePagePartialProps, EntityTablePageReducers, Optional, Utils } from "@crispico/foundation-react";
import { Filter } from "@crispico/foundation-react/components/CustomQuery/Filter";
import { TabRouterPane } from "@crispico/foundation-react/components/TabbedPage/TabbedPage";
import { FieldType } from "@crispico/foundation-react/entity_crud/FieldType";
import { ReduxReusableComponents, RRCProps } from "@crispico/foundation-react/reduxReusableComponents/ReduxReusableComponents";
import { Group, Item, RowLayer } from "@crispico/react-timeline-10000";
import lodash from 'lodash';
import moment from "moment-timezone";
import { GanttUtils } from "pages/gantt/GanttUtils";
import { TIME_HOUR_OFFSET } from "pages/gantt/XopsGantt";
import { Segment } from "semantic-ui-react";
import FlightFilterComponent, { FlightFilter, FlightFilterComponentRRC } from "./FlightFilterComponent";
import { FlightsAssignmentsPageRRC } from "./flightsAssignmentsPage/FlightsAssignmentsPage";
import { XopsGanttPartialProps, EntityTablePageWithGantt, EntityTablePageWithGanttOptions, EntityTablePageWithGanttState } from "pages/gantt/components/tables/EntityTablePageWithGantt";
import { Task } from "types";
import { getEntityGraphFields } from "@crispico/foundation-react/utils/GraphqlUtils";

const TASK_COMPACT_WIDTH_MINUTES = 15; // Width of tasks in compact mode in minutes
const TASK_COMPACT_GAP_MINUTES = 2; // Gap between tasks in compact mode in minutes

const flightTablePageOptionsEntityDescriptor = new EntityDescriptor({ name: "FlightTablePageOptions" })
    .addFieldDescriptor({ name: "showGantt", type: FieldType.boolean })
    .addFieldDescriptor({ name: "compactMode", type: FieldType.boolean })
    .addFieldDescriptor({ name: "expandTasks", type: FieldType.boolean })
    .addFieldDescriptor({ name: "date", type: FieldType.date, additionalFieldEditorProps: { format: Utils.dateFormat }, additionalFieldRendererProps: { format: Utils.dateFormat } })

const flightTablePageOptionsEntityDescriptorEmbedded = new EntityDescriptor({ name: "FlightTablePageOptions" })
    .addFieldDescriptor({ name: "showGantt", type: FieldType.boolean })
    .addFieldDescriptor({ name: "compactMode", type: FieldType.boolean })
    .addFieldDescriptor({ name: "expandTasks", type: FieldType.boolean })
    .addFieldDescriptor({ name: "date", type: FieldType.date, enabled: false, additionalFieldEditorProps: { format: Utils.dateFormat }, additionalFieldRendererProps: { format: Utils.dateFormat } })

export interface FlightTablePageOptions extends EntityTablePageWithGanttOptions {
    expandTasks: boolean
}

export class FlightTablePageState extends EntityTablePageWithGanttState<FlightTablePageOptions> {
    protected static readonly defaultOptions: FlightTablePageOptions = {
        ...EntityTablePageWithGanttState.defaultOptions,
        expandTasks: true,
        date: Utils.now().toISOString()
    };
    protected static readonly localStorageKey: string = 'FlightTablePage.options';

    flightFilter: Optional<FlightFilter> = undefined;
    showApplyModal: boolean = false;
}

export class FlightTablePageReducers<S extends FlightTablePageState = FlightTablePageState> extends EntityTablePageReducers<S> {
}

export type FlightTablePageProps = { getFullEntity?: (entityName: string, entity: any) => any, showFlightFilterComponent?: boolean } & EntityTablePagePartialProps & RRCProps<FlightTablePageState, FlightTablePageReducers>;

export class FlightTablePage<P extends FlightTablePageProps = FlightTablePageProps> extends EntityTablePageWithGantt<P> {

    static defaultProps = {
        ...EntityTablePage.defaultProps,
        showFlightFilterComponent: true
    }

    getFieldsToRequestAsString(fieldsToRequest: string[]) {
        const baseFields = "name departure airline number date initialDate comment color groundAgent arrivalDate departureDepartureDate";
        const taskBaseFields = "id taskType { id color name } missionType { id color }";
        const missionFieldsMinimal = "mission { id }";
        const missionFieldsFull = "mission { id type { id color name } inactivityType { id color name } equipmentResource { id } startTime endTime type { color } comment status finishedByUser color startAddress { id name } endAddress { id name } humanResource { id identifier firstName lastName } equipmentType { id name } }";
        const taskAdditionalFields = "comment actualStartTime actualEndTime name quantity offloadStatus startAddress { id name } endAddress { id name } requiredEquipmentResourceQualificationType equipmentResourceFillPercentage }";

        const missionPart = this.props.embeddedMode ? ` ${missionFieldsMinimal}` : ` ${missionFieldsFull}`;
        const tasksString = `tasks { ${taskBaseFields}${missionPart} ${taskAdditionalFields}`;

        return super.getFieldsToRequestAsString(fieldsToRequest) + ` ${baseFields} ${tasksString}`;
    }

    componentDidUpdate(prevProps: P) {
        super.componentDidUpdate(prevProps);
        if (prevProps && !lodash.isEqual(prevProps.s.flightFilter, this.props.s.flightFilter)) {
            this.refresh();
        }
    }

    async componentDidUpdateInternal(prevProps?: P) {
        super.componentDidUpdateInternal(prevProps);
    }

    adjustFilterBeforeLoad(filter: Filter): Filter {
        filter = super.adjustFilterBeforeLoad(filter);
        let filterWithDate = this.props.s.options.date
            ? Filter.createComposed(FilterOperators.forComposedFilter.and, [filter,
                Filter.create("date", FilterOperators.forDate.dayOf, this.props.s.options.date)])
            : filter;

        if (this.props.embeddedMode) {
            filterWithDate = Filter.createComposed(FilterOperators.forComposedFilter.and, [
                filterWithDate,
                Filter.create("showFlightInGantt", FilterOperators.forBoolean.equals, "true")
            ]);
        }

        let newFilter = FlightFilterComponent.createFilter(this.props.s.flightFilter);
        if (newFilter.filters!.length == 0) {
            return filterWithDate;
        }
        newFilter.filters!.push(filterWithDate);
        return newFilter;
    }

    protected getExtraTabPanes(): (TabRouterPane | null)[] {
        let extraTabPanes = [...super.getExtraTabPanes(),
        {
            routeProps: { path: "/flightsAssignments" },
            menuItemProps: { icon: "plane", content: _msg("FlightsAssignmentsPage.title") },
            render: () => <FlightsAssignmentsPageRRC id='flightsAssignments' />
        }
        ];
        return extraTabPanes;
    }

    protected getOptionsEntityDescriptor(): EntityDescriptor {
        return this.props.embeddedMode ? flightTablePageOptionsEntityDescriptorEmbedded : flightTablePageOptionsEntityDescriptor;
    }

    protected getFullTask(flight: any, task: any) {
        let fullTask = this.props.getFullEntity ? this.props.getFullEntity("Task", task) : task;
        return { ...fullTask, taskGroup: flight };
    }

    protected getTimelineData(): { groups: Group[], items: (Item & { entity: any })[], rowLayers: RowLayer[], startDate: number, endDate: number } {
        const groups: Group[] = [];
        const items: (Item & { entity: any })[] = [];
        const rowLayers: RowLayer[] = [];
        const entities = this.entityTableSimpleRef.current?.getEntities();
        let startDate: number | undefined;
        let endDate: number | undefined;
        let date: number | undefined;
        entities?.map(flight => {
            groups.push({ id: flight.id });
            date = moment(flight.date).valueOf();
            startDate = !startDate || startDate > date ? date : startDate;
            endDate = !endDate || endDate < date ? date : endDate;

            // Sort tasks by taskType first, then by actualStartTime
            const sortedTasks = flight.tasks?.slice().sort((a: Task, b: Task) => {
                const taskTypeComparison = (a.taskType?.name || '').localeCompare(b.taskType?.name || '');
                return taskTypeComparison !== 0 ? taskTypeComparison : a.actualStartTime - b.actualStartTime;
            }) || [];

            if (this.props.s.options?.expandTasks) {
                // Normal behavior: use actual start and end times
                sortedTasks.forEach((t: Task) => {
                    items.push({
                        key: GanttUtils.toEntityUid("Task", t.id),
                        row: flight.id,
                        start: t.actualStartTime,
                        end: t.actualEndTime,
                        entity: this.getFullTask(flight, t)
                    });
                });
            } else {
                // Compact behavior: position tasks side by side starting from flight date with 15 minutes width and 2 minutes gap
                const flightDate = moment(flight.date).valueOf();

                // Convert minutes to milliseconds
                const taskWidthMs = TASK_COMPACT_WIDTH_MINUTES * 60 * 1000; // 15 minutes in milliseconds
                const gapMs = TASK_COMPACT_GAP_MINUTES * 60 * 1000; // 2 minutes in milliseconds

                sortedTasks.forEach((t: Task, index: number) => {
                    const taskStart = flightDate + (index * (taskWidthMs + gapMs));
                    const taskEnd = taskStart + taskWidthMs;
                    items.push({
                        key: GanttUtils.toEntityUid("Task", t.id),
                        row: flight.id,
                        start: taskStart,
                        end: taskEnd,
                        entity: this.getFullTask(flight, t)
                    });
                });
            }
            // ground time layer
            rowLayers.push({
                rowNumber: flight._index,
                start: moment(flight.arrivalDate || flight.date).valueOf(),
                end: moment(flight.departureDepartureDate || flight.date).valueOf(),
                style: { background: `lightgrey`, opacity: 0.5 },
            });
        });
        // use options.date if available, otherwise use calculated flight dates
        if (this.props.s.options?.date) {
            startDate = moment(this.props.s.options.date).startOf('day').add(-TIME_HOUR_OFFSET, 'hour').valueOf();
            endDate = moment(this.props.s.options.date).endOf('day').add(TIME_HOUR_OFFSET, 'hour').valueOf();
        } else {
            // fallback to calculated flight dates
            startDate = moment(startDate).add(-TIME_HOUR_OFFSET, 'hour').startOf('day').valueOf();
            endDate = moment(startDate).add(-TIME_HOUR_OFFSET, 'hour').endOf('day').valueOf();
        }
        return { groups, items, rowLayers, startDate, endDate };
    }

    protected getXopsGanttProps(): XopsGanttPartialProps {
        const { groups, items, rowLayers, startDate, endDate } = this.getTimelineData();
        return { startDate, endDate, groups, items, rowLayers };
    }

    protected renderCompactBar(): JSX.Element {
        return <>{super.renderCompactBar()}
            {this.props.showFlightFilterComponent ? <Segment className="less-padding less-margin-top-bottom flex-container-row flex-center flex-wrap gap5">
                <FlightFilterComponentRRC id="flightFilterComponent"
                    showDepartureFilter={true} showFlightTypeFilter={true} showLastKnownDateTypeFilter={true}
                    onFilterChange={(flightFilter) => this.props.r.setInReduxState({ flightFilter })} />
            </Segment> : null}
        </>
    }
}

export const FlightTablePageRRC = ReduxReusableComponents.connectRRC(FlightTablePageState, FlightTablePageReducers, FlightTablePage);
